// UsersAndPermissions.jsx — unified Settings module that merges the two
// previously-separate sections ("الأدوار والصلاحيات" + "المستخدمون والحساب")
// into a single tabbed panel for a cleaner admin experience.
//
// Tabs (admin only sees all three):
//   1. المستخدمون     → UsersPanel (CRUD + per-user page permissions)
//   2. الأدوار        → RolePermissionsPanel (role × permission matrix)
//   3. حسابي          → ChangePassword + 2FA + data sharing (any user)
//
// Non-admin users only see tab 3 ("حسابي") since the first two are admin-only.
//
// Why a wrapper instead of editing App.js further: keeps Settings markup small
// and lets the user feature flag this view if needed later.
import React, { lazy, Suspense, useState } from "react";
import UsersPanel from "./UsersPanel";

const RolePermissionsPanel = lazy(() => import("./RolePermissionsPanel"));
const TwoFAPanel = lazy(() => import("./TwoFAPanel"));
const ChangePasswordCard = lazy(() => import("./ChangePasswordCard"));
const SendCapsCard = lazy(() => import("./SendCapsCard"));

const TABS_BASE = [
  { id: "users", label: "المستخدمون", icon: "👥", adminOnly: true },
  { id: "roles", label: "الأدوار والصلاحيات", icon: "🛡️", adminOnly: true },
  { id: "send_caps", label: "سقوف الإرسال", icon: "📤", adminOnly: true },
  { id: "account", label: "حسابي", icon: "🔐", adminOnly: false },
];

const Loader = () => (
  <div style={{ padding: 16, fontSize: 12, color: "var(--muted)" }} data-testid="users-perm-loading">
    جاري التحميل…
  </div>
);

export default function UsersAndPermissions({ currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const tabs = TABS_BASE.filter((t) => isAdmin || !t.adminOnly);
  const [active, setActive] = useState(tabs[0].id);

  return (
    <div className="users-perm-wrap" data-testid="users-perm-panel">
      {/* Tabbar — horizontally scrollable on mobile, no ugly wrap */}
      <div
        role="tablist"
        aria-label="إدارة المستخدمين والصلاحيات"
        className="users-perm-tabs"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={active === t.id}
            type="button"
            onClick={() => setActive(t.id)}
            className={`users-perm-tab${active === t.id ? " is-active" : ""}`}
            data-testid={`users-perm-tab-${t.id}`}
          >
            <span aria-hidden="true" style={{ marginInlineEnd: 6 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      <div className="users-perm-panel" role="tabpanel">
        {active === "users" && isAdmin && (
          <UsersPanel currentUser={currentUser} />
        )}

        {active === "roles" && isAdmin && (
          <Suspense fallback={<Loader />}>
            <RolePermissionsPanel />
          </Suspense>
        )}

        {active === "send_caps" && isAdmin && (
          <Suspense fallback={<Loader />}>
            <SendCapsCard />
          </Suspense>
        )}

        {active === "account" && (
          <div className="users-perm-account">
            <Suspense fallback={<Loader />}>
              <ChangePasswordCard />
            </Suspense>
            <Suspense fallback={<Loader />}>
              <TwoFAPanel />
            </Suspense>
            <div className="set-sec" style={{ marginTop: 10 }}>
              <h4>💾 مشاركة البيانات بين الأجهزة</h4>
              <p style={{ fontSize: 12, color: "var(--gy)", marginBottom: 8, lineHeight: 1.8 }}>
                البيانات محفوظة تلقائياً على السحابة وتُستعاد فور تسجيل الدخول من أي جهاز.<br />
                التصدير/الاستيراد لأغراض النسخ الاحتياطية فقط.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
